import {ErrorHandler, OnCompleteCallback} from "../../../utils";
import {FC, useState} from "react";
import {UpdateTelecomNumbersInput} from "@common-core/coat-operational-hierarchy-appsync-model";
import {ModalWithAlert, useAlert} from "../../../alerting";
import {useTranslation} from "react-i18next";
import {useOperationalEntity} from "../../context";
import {useGraphqlLazyQuery, useGraphqlMutation} from "../../../amplify/hooks";
import {
  lookupOperationalEntity as lookupOperationalEntityQuery,
  updateOperationalEntityTelecomNumbers as updateOperationalEntityTelecomNumbersMutation
} from "../../../amplify/graphql";
import {
  LookupOperationalEntity,
  LookupOperationalEntityData,
  LookupOperationalEntityVariables,
  UpdateOperationalEntityTelecomNumbers,
  UpdateOperationalEntityTelecomNumbersData,
  UpdateOperationalEntityTelecomNumbersVariables,
  assertOperationalEntityPresent
} from "../../backend";
import {Endpoints} from "../../../runtime";
import {RadioButtonList} from "@interstate/components/RadioButtonList";
import "./EditTelecomNumbersModal.scss";
import {
  ClientPhoneNumber,
  ClientPhoneNumberMap,
  matchingClientPhoneNumber,
  toRadioButtonOption
} from "./phones";

export const EditTelecomNumbersModal: FC<{
  currentNumber?: string;
  availableNumbers: ClientPhoneNumber[];
  onComplete: OnCompleteCallback;
}> = ({currentNumber, availableNumbers, onComplete}) => {
  const {t} = useTranslation();
  const {alert, clearAlert, createErrorAlert} = useAlert({
    errorTitleKey: "toast.backend-error"
  });
  const [working, setWorking] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>(
    () => availableNumbers.find(matchingClientPhoneNumber(currentNumber))?.key
  );
  const [availableNumbersMap] = useState<ClientPhoneNumberMap>(() =>
    availableNumbers.reduce((accumulator, phone) => {
      accumulator[phone.key] = phone;
      return accumulator;
    }, {} as ClientPhoneNumberMap)
  );
  const {entity, setEntity} = useOperationalEntity();

  const [lookUpOperationalEntity] = useGraphqlLazyQuery<
    LookupOperationalEntityData,
    LookupOperationalEntityVariables
  >({
    document: LookupOperationalEntity,
    amplifyQuery: lookupOperationalEntityQuery,
    context: {endpoint: Endpoints.APPSYNC},
    fetchPolicy: "network-only"
  });

  const [updateTelecomNumbers] = useGraphqlMutation<
    UpdateOperationalEntityTelecomNumbersData,
    UpdateOperationalEntityTelecomNumbersVariables
  >({
    document: UpdateOperationalEntityTelecomNumbers,
    amplifyMutation: updateOperationalEntityTelecomNumbersMutation,
    context: {endpoint: Endpoints.APPSYNC}
  });

  const clearErrors = (): void => {
    clearAlert();
  };

  const handleError: ErrorHandler = (error: any) => {
    createErrorAlert(error);
  };

  const refreshEntity = async (): Promise<void> => {
    return await lookUpOperationalEntity({variables: {id: entity.id}})
      .then(assertOperationalEntityPresent)
      .then(setEntity);
  };

  const saveChanges = async () => {
    setWorking(true);
    clearErrors();
    const telecomNumbersInput: UpdateTelecomNumbersInput = {
      entityId: entity.id,
      telecomNumbers: [availableNumbersMap[selectedValue!].number]
    };
    await updateTelecomNumbers({variables: {telecomNumbersInput}})
      .then(result => {
        if (result.data?.result.success === false) {
          throw new Error(t("common-errors.backend-error"));
        }
      })
      .then(refreshEntity)
      // If the update call returns an error, give the user the opportunity to try again
      .then(() => onComplete(true))
      .catch(handleError)
      .finally(() => setWorking(false));
  };

  const cancelChanges = () => {
    onComplete(false);
  };

  return (
    <ModalWithAlert
      id={"edit-telecom-numbers-modal"}
      data-testid={"edit-telecom-numbers-modal"}
      show={true}
      size={"large"}
      header={t("modal.header.edit-telecom-numbers")}
      alert={alert}
      footer={{
        primary: {
          action: saveChanges,
          label: t("common-actions.save-changes"),
          isLoading: working,
          disabled: !selectedValue
        },
        options: {
          action: cancelChanges,
          label: t("common-actions.cancel"),
          isLoading: working,
          buttonStyle: "tertiary"
        }
      }}
      onHide={cancelChanges}>
      <RadioButtonList
        id={"telecom-numbers-radio-list"}
        data-testid={"telecom-numbers-radio-list"}
        label={t("modal.text-input.edit-telecom-numbers")}
        name={"edit-telecom-numbers-list"}
        options={availableNumbers.map(toRadioButtonOption)}
        value={selectedValue}
        onChange={event => setSelectedValue(event.target.value)}
      />
    </ModalWithAlert>
  );
};
